import { SessionActivity } from '@shared/entities';
import {
  MealActivityTypes,
  RolePerformerType,
  RoleType,
  SessionActivityType,
  TravelActivityTypes,
  WorkActivityTypes,
} from '@shared/types';
import { Dayjs } from 'dayjs';

/**
 * Activity Types
 */
export const getIsWork = (activityType: SessionActivityType) =>
  WorkActivityTypes.has(activityType);
export const getIsTravel = (activityType: SessionActivityType) =>
  TravelActivityTypes.has(activityType);
export const getIsTravelTo = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.TravelToLocation;
export const getIsTravelToAfterMidnight = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.TravelToLocationAfterMidnight;
export const getIsTravelFrom = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.TravelFromLocation;
export const getIsTravelFromAfterMidnight = (
  activityType: SessionActivityType,
) => activityType === SessionActivityType.TravelFromLocationAfterMidnight;
export const getIsMeal = (activityType: SessionActivityType) =>
  MealActivityTypes.has(activityType);
export const getIsWardrobeChange = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.WardrobeChange;
export const getIsFitting = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.Fitting;
export const getIsRecordingSession = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.VoiceOver ||
  activityType === SessionActivityType.RecordingSession;
export const getIsAFMRecordingSession = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.AFMRecordingSession;

/**
 * Work Time Types
 */
export const getIsNightWorkTime = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.WorkTimeNight;
export const getIsLateNightWorkTime = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.WorkTimeLateNight;
export const getIsSaturdayWorkTime = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.WorkTimeSaturday;
export const getIsSundayWorkTime = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.WorkTimeSunday;
export const getIsHolidayWorkTime = (activityType: SessionActivityType) =>
  activityType === SessionActivityType.WorkTimeHoliday;

/**
 * Role Types
 */
export const getIsOnScreenPrincipal = (rolePerformerType?: RolePerformerType) =>
  rolePerformerType === RolePerformerType.OnCameraPrincipal;
export const getIsOnCameraGroup = (rolePerformerType?: RolePerformerType) =>
  rolePerformerType === RolePerformerType.OnCameraGroup;
export const getIsOffScreenPrincipal = (
  rolePerformerType?: RolePerformerType,
) => rolePerformerType === RolePerformerType.OffCameraPrincipal;
export const getIsOffCameraGroup = (rolePerformerType?: RolePerformerType) =>
  rolePerformerType === RolePerformerType.OffCameraGroup;
export const getIsPrincipal = (rolePerformerType?: RolePerformerType) =>
  getIsOnScreenPrincipal(rolePerformerType) ||
  getIsOffScreenPrincipal(rolePerformerType);
export const getIsExtra = (rolePerformerType?: RolePerformerType) =>
  rolePerformerType === RolePerformerType.Extra;
export const getIsAFMPerformer = (rolePerformerType?: RolePerformerType) =>
  rolePerformerType === RolePerformerType.AFM;

export const getIsDancer = (roleType: RoleType) =>
  roleType === RoleType.SpecialtyDancer;
export const getIsPilot = (roleType: RoleType) => roleType === RoleType.Pilot;
export const getIsStuntPerformer = (roleType: RoleType) =>
  roleType === RoleType.StuntPerformer;
export const getIsSinger = (roleType: RoleType) => roleType === RoleType.Singer;
export const getIsSpeaker = (roleType: RoleType) =>
  roleType === (RoleType.Narrator || RoleType.Announcer || RoleType.Speaker);

/**
 * Looks up Session Activity by time.
 * @param sessionActivities
 * @param timePointer
 * @returns SessionActivity
 */
export const getActivityByTime = (
  sessionActivities: SessionActivity[],
  timePointer: Dayjs,
): SessionActivity | undefined => {
  return sessionActivities[
    sessionActivities.findIndex((sessionActivity) => {
      // Equivalent to moment's `.isBetween(start, end, undefined, '[)')` - start-inclusive,
      // end-exclusive. dayjs has no isBetween of its own; matches apps/web/src/utils/time.ts's
      // existing hand-rolled convention for this rather than adding a dayjs plugin.
      const start = timePointer.isSame(sessionActivity.start_time) || timePointer.isAfter(sessionActivity.start_time);
      const end = timePointer.isBefore(sessionActivity.end_time);
      return start && end;
    })
  ];
};
